import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import SoftButton from "components/SoftButton";
import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useRoles } from "hooks/useRoles";
import { updateUserRoles } from "api/RolesApi";
import { toast } from "react-toastify";

const TransferOwnershipDialog = ({ open, onClose, targetUser, onUsersChanged }) => {
  const { user } = useAuth0();
  const { roles } = useRoles(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleTransferOwnership = async () => {
    if (isLoading || !targetUser?.auth0Id) return;
    const ownerRoleId = roles.find((role) => role.name?.toLowerCase() === "owner")?.id;
    const adminRoleId = roles.find((role) => role.name?.toLowerCase() === "admin")?.id;
    if (!ownerRoleId || !adminRoleId) {
      toast.error("Owner and Admin roles must be available to transfer ownership.");
      return;
    }
    setIsLoading(true);
    try {
      const newOwner = await updateUserRoles(user.stripeId, targetUser.auth0Id, ownerRoleId);
      if (!newOwner?.isSuccessful) throw new Error("Unable to assign the new owner.");
      const previousOwner = await updateUserRoles(user.stripeId, user.cxfUserId, adminRoleId);
      if (!previousOwner?.isSuccessful) throw new Error("The new owner was assigned, but your role could not be changed to Admin. Please refresh and review the user roles.");
      toast.success("Owner role transferred successfully!");
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      try {
        await onUsersChanged?.();
      } catch {
        toast.error("Unable to refresh users. Please reload the user list.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
  <Dialog fullWidth maxWidth="sm" open={open} onClose={isLoading ? undefined : onClose} aria-labelledby="transfer-ownership-title">
    <DialogTitle id="transfer-ownership-title">Transfer Ownership</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to make <strong>{targetUser?.username}</strong> the owner?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <SoftButton onClick={onClose} variant="outlined" color="info" disabled={isLoading}>Cancel</SoftButton>
      <SoftButton onClick={handleTransferOwnership} variant="contained" color="error" disabled={isLoading}>
        {isLoading ? <CircularProgress size={20} color="inherit" /> : "Transfer Ownership"}
      </SoftButton>
    </DialogActions>
  </Dialog>
);
};

export default TransferOwnershipDialog;
